import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';

// Types
interface Question {
  id: number;
  text: string;
  options: string[];
  scoring: Record<string, string>;
}
interface ProfileResult {
  id: string;
  icon: string;
  title: string;
  description: string;
  meaning: string;
  helpPoints: string[];
  planIncluded: string[];
  recommendedPlan: string;
}

// Quiz data
const questions: Question[] = [{
  id: 1,
  text: "¿Cómo describirías tu alimentación actual?",
  options: ["Como bastante variado, pero no sé si las porciones son correctas", "Como lo mismo casi todos los días por falta de ideas", "Como fuera de casa con frecuencia (restaurantes, comida rápida)", "Como cuando tengo tiempo, a veces salto comidas"],
  scoring: {
    "0": "DESORGANIZADO",
    "1": "RUTINARIO",
    "2": "SOCIAL",
    "3": "CAÓTICO"
  }
}, {
  id: 2,
  text: "¿Cuál es tu principal objetivo con la nutrición?",
  options: ["Sentirme con más energía durante el día", "Aprender a comer de forma más saludable", "Mejorar mi relación con la comida", "Tener un plan que se adapte a mi rutina ocupada"],
  scoring: {
    "0": "ENERGÍA",
    "1": "EDUCATIVO",
    "2": "EMOCIONAL",
    "3": "PRÁCTICO"
  }
}, {
  id: 3,
  text: "¿Cómo es tu rutina de comidas?",
  options: ["Tengo horarios fijos y casi siempre como a la misma hora", "Mis horarios varían mucho según el día", "Como cuando tengo hambre, sin horarios específicos", "Intento tener horarios pero no siempre los cumplo"],
  scoring: {
    "0": "ESTRUCTURADO",
    "1": "VARIABLE",
    "2": "INTUITIVO",
    "3": "FLEXIBLE"
  }
}, {
  id: 4,
  text: "¿Cuánto tiempo tienes para cocinar entre semana?",
  options: ["Menos de 30 minutos al día", "Entre 30 minutos y 1 hora al día", "Más de 1 hora, me gusta cocinar", "Casi nada, prefiero cosas rápidas o preparadas"],
  scoring: {
    "0": "RÁPIDO",
    "1": "MODERADO",
    "2": "COCINERO",
    "3": "MÍNIMO"
  }
}, {
  id: 5,
  text: "¿Con qué frecuencia comes frutas y verduras?",
  options: ["Todos los días, en la mayoría de mis comidas", "Varios días a la semana, pero no todos los días", "De vez en cuando, no es lo que más como", "Muy rara vez, me cuesta incluirlas"],
  scoring: {
    "0": "CONSCIENTE",
    "1": "INTERMEDIO",
    "2": "PRINCIPIANTE",
    "3": "BÁSICO"
  }
}, {
  id: 6,
  text: "¿Con quién comes habitualmente?",
  options: ["Solo/a la mayoría de las veces", "Con mi familia o pareja", "Depende del día, a veces solo/a y a veces acompañado/a", "Con compañeros de trabajo o amigos"],
  scoring: {
    "0": "INDIVIDUAL",
    "1": "FAMILIAR",
    "2": "MIXTO",
    "3": "SOCIAL"
  }
}, {
  id: 7,
  text: "¿Tienes alguna restricción o preferencia alimentaria?",
  options: ["No, como de todo", "Evito algunos alimentos por preferencia personal", "Tengo intolerancia o alergia a algo específico", "Sigo una dieta específica (vegetariana, sin gluten, etc.)"],
  scoring: {
    "0": "ABIERTO",
    "1": "SELECTIVO",
    "2": "INTOLERANTE",
    "3": "RESTRICTIVO"
  }
}, {
  id: 8,
  text: "¿Has trabajado antes con un nutriólogo o dietista?",
  options: ["Sí, varias veces", "Sí, una vez pero no continué", "No, esta sería mi primera vez", "No, pero he seguido planes de internet"],
  scoring: {
    "0": "EXPERIMENTADO",
    "1": "INTENTÓ",
    "2": "NOVATO",
    "3": "AUTODIDACTA"
  }
}, {
  id: 9,
  text: "¿Cuánto tiempo puedes dedicarle a mejorar tu alimentación?",
  options: ["Estoy 100% comprometido/a, es una prioridad", "Puedo dedicarle tiempo, pero tengo otras prioridades también", "Tengo poco tiempo, necesito algo muy práctico", "Quiero empezar despacio y ver cómo me va"],
  scoring: {
    "0": "COMPROMETIDO",
    "1": "BALANCEADO",
    "2": "LIMITADO",
    "3": "EXPLORATORIO"
  }
}, {
  id: 10,
  text: "¿Cuál es tu mayor dificultad con la alimentación?",
  options: ["No sé qué comer o cómo prepararlo", "No tengo tiempo para cocinar o planear", "Me cuesta mantener la constancia", "No veo resultados cuando intento cambiar"],
  scoring: {
    "0": "DESINFORMACIÓN",
    "1": "TIEMPO",
    "2": "CONSTANCIA",
    "3": "FRUSTRACIÓN"
  }
}];

// Profile definitions
const profiles: Record<string, ProfileResult> = {
  OCUPADO: {
    id: "OCUPADO",
    icon: "⏱️",
    title: "El Ocupado",
    description: "Según tus respuestas, tu principal desafío es encontrar tiempo para comer bien dentro de una rutina variable.",
    meaning: "Necesitas un plan que se adapte a tu ritmo de vida, no al revés. Las dietas rígidas no funcionan para ti porque tus horarios cambian. Lo que SÍ funciona es tener opciones rápidas y flexibles que puedas seguir aunque tengas poco tiempo.",
    helpPoints: ["Diseñar comidas que se preparen en menos de 30 minutos", "Crear un \"menú de emergencia\" para días caóticos", "Organizar tu semana para que no improvises tanto"],
    planIncluded: ["Análisis detallado de tu caso específico", "Menú semanal adaptado a tu disponibilidad de tiempo", "Lista de compras organizada", "Recetas rápidas paso a paso", "Recomendación de qué plan se ajusta mejor a ti"],
    recommendedPlan: "mensual"
  },
  DESORIENTADO: {
    id: "DESORIENTADO",
    icon: "🧭",
    title: "El Desorientado",
    description: "Según tus respuestas, quieres mejorar tu alimentación pero no tienes claro por dónde empezar ni qué funciona realmente.",
    meaning: "Has probado cosas de internet sin resultados. El problema no es tu fuerza de voluntad — es que no tienes información clara adaptada a TI. Lo que necesitas es educación práctica, no otra dieta genérica.",
    helpPoints: ["Entender qué comer según TUS necesidades, no lo que dice internet", "Aprender a identificar qué te funciona y qué no", "Tener un plan claro con pasos concretos"],
    planIncluded: ["Explicación clara de qué necesita tu cuerpo", "Plan de alimentación de 14 días con recetas", "Guía de qué alimentos priorizar y cuáles moderar", "Consejos para mantener el plan sin abandonar", "Recomendación de qué plan se ajusta mejor a ti"],
    recommendedPlan: "basico"
  },
  CONSCIENTE: {
    id: "CONSCIENTE",
    icon: "⭐",
    title: "El Consciente",
    description: "Según tus respuestas, ya tienes buenos hábitos y conocimiento sobre nutrición. Lo que buscas es optimizar y personalizar aún más.",
    meaning: "Estás listo para un plan profesional de largo plazo. No necesitas que te convenzan de comer mejor — necesitas un experto que ajuste tu alimentación a tus objetivos específicos y te acompañe en el proceso.",
    helpPoints: ["Afinar tu alimentación para objetivos específicos", "Diseñar un plan progresivo con seguimiento", "Ajustar según tus resultados y necesidades"],
    planIncluded: ["Análisis avanzado de tus hábitos actuales", "Plan de alimentación progresivo de 14 días", "Recomendaciones para optimizar tu nutrición", "Estrategias de seguimiento y ajuste", "Recomendación: Te beneficiarías del programa mensual con seguimiento"],
    recommendedPlan: "mensual"
  }
};

// Scoring logic
function calculateProfile(answers: number[]): string {
  const scores: Record<string, number> = {};
  answers.forEach((answerIndex, questionIndex) => {
    const question = questions[questionIndex];
    const category = question.scoring[answerIndex.toString()];
    scores[category] = (scores[category] || 0) + 1;
  });
  const ocupadoScore = (scores.CAÓTICO || 0) + (scores.PRÁCTICO || 0) + (scores.RÁPIDO || 0) + (scores.TIEMPO || 0) + (scores.LIMITADO || 0) + (scores.MÍNIMO || 0);
  const desorientadoScore = (scores.DESORGANIZADO || 0) + (scores.DESINFORMACIÓN || 0) + (scores.PRINCIPIANTE || 0) + (scores.NOVATO || 0) + (scores.AUTODIDACTA || 0) + (scores.BÁSICO || 0);
  const conscienteScore = (scores.CONSCIENTE || 0) + (scores.COMPROMETIDO || 0) + (scores.ESTRUCTURADO || 0) + (scores.EDUCATIVO || 0) + (scores.EXPERIMENTADO || 0) + (scores.COCINERO || 0);
  if (ocupadoScore >= 3 && ocupadoScore >= desorientadoScore && ocupadoScore >= conscienteScore) {
    return 'OCUPADO';
  } else if (desorientadoScore >= 3 && desorientadoScore >= conscienteScore) {
    return 'DESORIENTADO';
  } else if (conscienteScore >= 3) {
    return 'CONSCIENTE';
  }
  return 'OCUPADO'; // fallback
}
export function Quiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [profileResult, setProfileResult] = useState<ProfileResult | null>(null);
  const progress = (currentQuestion + 1) / questions.length * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canGoBack = currentQuestion > 0;
  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };
  const handleNext = () => {
    if (selectedOption === null) return;
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedOption;
    setAnswers(newAnswers);
    if (isLastQuestion) {
      // Calculate result
      setIsCalculating(true);
      setTimeout(() => {
        const profileId = calculateProfile([...newAnswers]);
        setProfileResult(profiles[profileId]);
        setIsCalculating(false);
        setShowResult(true);
      }, 2000);
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(answers[currentQuestion + 1] ?? null);
    }
  };
  const handleBack = () => {
    if (canGoBack) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(answers[currentQuestion - 1] ?? null);
    }
  };
  if (isCalculating) {
    return <div className="min-h-screen bg-primary flex items-center justify-center px-4" data-id="element-210">
        <div className="text-center" data-id="element-211">
          <Loader2 className="w-16 h-16 text-accent animate-spin mx-auto mb-6" data-id="element-212" />
          <h2 className="text-2xl font-serif font-medium text-text-primary mb-2" data-id="element-213">
            Analizando tus respuestas...
          </h2>
          <p className="text-text-secondary" data-id="element-214">
            Identificando tu perfil nutricional
          </p>
        </div>
      </div>;
  }
  if (showResult && profileResult) {
    return <div className="min-h-screen bg-primary py-12 px-4" data-id="element-215">
        <div className="max-w-3xl mx-auto" data-id="element-216">
          {/* Result Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg mb-8" data-id="element-217">
            <div className="text-6xl mb-6 text-center" data-id="element-218">{profileResult.icon}</div>
            
            <h2 className="text-3xl font-serif font-medium text-center mb-4 text-text-primary" data-id="element-219">
              Tu perfil: <span className="text-accent" data-id="element-220">{profileResult.title}</span>
            </h2>

            <p className="text-lg text-text-secondary text-center mb-8" data-id="element-221">
              {profileResult.description}
            </p>

            <div className="bg-primary/30 rounded-xl p-6 mb-8" data-id="element-222">
              <h3 className="text-lg font-semibold text-text-primary mb-3" data-id="element-223">
                🎯 Lo que esto significa:
              </h3>
              <p className="text-text-secondary leading-relaxed" data-id="element-224">
                {profileResult.meaning}
              </p>
            </div>

            <div className="mb-8" data-id="element-225">
              <h3 className="text-lg font-semibold text-text-primary mb-4" data-id="element-226">
                ✓ Un nutriólogo puede ayudarte a:
              </h3>
              <ul className="space-y-3" data-id="element-227">
                {profileResult.helpPoints.map((point, index) => <li key={index} className="flex items-start gap-3" data-id="element-228">
                    <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" data-id="element-229" />
                    <span className="text-text-secondary" data-id="element-230">{point}</span>
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Paywall */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border-2 border-accent" data-id="element-231">
            <div className="text-center mb-6" data-id="element-232">
              <h3 className="text-2xl font-serif font-medium text-text-primary mb-2" data-id="element-233">
                Accede a tu plan personalizado completo
              </h3>
              <p className="text-text-secondary" data-id="element-234">
                Descubre exactamente qué comer y cómo organizarte
              </p>
            </div>

            <div className="bg-primary/20 rounded-xl p-6 mb-8" data-id="element-235">
              <h4 className="font-semibold text-text-primary mb-4" data-id="element-236">
                Tu plan incluye:
              </h4>
              <ul className="space-y-3" data-id="element-237">
                {profileResult.planIncluded.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-238">
                    <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" data-id="element-239" />
                    <span className="text-text-secondary text-sm" data-id="element-240">{item}</span>
                  </li>)}
              </ul>
            </div>

            <button onClick={() => {
            // TODO: Redirect to Stripe checkout
            window.location.href = '/checkout?plan=basico';
          }} className="w-full bg-accent hover:bg-accent/90 text-white font-semibold py-4 px-8 rounded-full transition-all flex items-center justify-center gap-2 mb-4" data-id="element-241">
              Ver mi plan completo por $499 MXN
              <ArrowRight className="w-5 h-5" data-id="element-242" />
            </button>

            <div className="text-center space-y-2 text-sm text-text-secondary" data-id="element-243">
              <p className="flex items-center justify-center gap-2" data-id="element-244">
                💳 Pago seguro con Stripe · Acepta tarjeta, OXXO o SPEI
              </p>
              <p className="flex items-center justify-center gap-2" data-id="element-245">
                🔒 Garantía de 7 días · Si no te aporta valor, te devolvemos el dinero
              </p>
            </div>
          </motion.div>
        </div>
      </div>;
  }
  const question = questions[currentQuestion];
  return <div className="min-h-screen bg-primary py-12 px-4" data-id="element-246">
      <div className="max-w-2xl mx-auto" data-id="element-247">
        {/* Progress bar */}
        <div className="mb-8" data-id="element-248">
          <div className="flex justify-between items-center mb-2" data-id="element-249">
            <span className="text-sm font-medium text-text-secondary" data-id="element-250">
              Pregunta {currentQuestion + 1} de {questions.length}
            </span>
            <span className="text-sm font-medium text-accent" data-id="element-251">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-252">
            <motion.div className="h-full bg-accent" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-253" />
          </div>
        </div>

        {/* Question card */}
        <AnimatePresence mode="wait" data-id="element-254">
          <motion.div key={currentQuestion} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg mb-6" data-id="element-255">
            <h2 className="text-2xl lg:text-3xl font-serif font-medium text-text-primary mb-8" data-id="element-256">
              {question.text}
            </h2>

            <div className="space-y-4" data-id="element-257">
              {question.options.map((option, index) => <button key={index} onClick={() => handleOptionSelect(index)} className={`w-full text-left p-5 rounded-xl border-2 transition-all ${selectedOption === index ? 'border-accent bg-accent/5' : 'border-secondary hover:border-accent/30 hover:bg-primary/30'}`} data-id="element-258">
                  <div className="flex items-start gap-4" data-id="element-259">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedOption === index ? 'border-accent bg-accent' : 'border-secondary'}`} data-id="element-260">
                      {selectedOption === index && <Check className="w-4 h-4 text-white" data-id="element-261" />}
                    </div>
                    <span className="text-text-primary" data-id="element-262">{option}</span>
                  </div>
                </button>)}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex gap-4" data-id="element-263">
          <button onClick={handleBack} disabled={!canGoBack} className={`flex items-center gap-2 px-6 py-3 rounded-full border-2 transition-all ${canGoBack ? 'border-accent text-accent hover:bg-accent/5' : 'border-secondary text-text-secondary opacity-50 cursor-not-allowed'}`} data-id="element-264">
            <ArrowLeft className="w-5 h-5" data-id="element-265" />
            Anterior
          </button>

          <button onClick={handleNext} disabled={selectedOption === null} className={`flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-full transition-all ${selectedOption !== null ? 'bg-accent hover:bg-accent/90 text-white' : 'bg-secondary text-text-secondary cursor-not-allowed'}`} data-id="element-266">
            {isLastQuestion ? 'Ver resultado' : 'Siguiente'}
            <ArrowRight className="w-5 h-5" data-id="element-267" />
          </button>
        </div>

        {/* Info text */}
        <p className="text-center text-sm text-text-secondary mt-6" data-id="element-268">
          Sin compromiso · No pedimos tarjeta
        </p>
      </div>
    </div>;
}